import { type ForwardedRef, type ReactNode, forwardRef, isValidElement } from 'react'

import Box, { type BoxProps } from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useId from '@mui/material/utils/useId'

function SectionHeader({ label, actions }: { label: string; actions?: ReactNode }) {
    const labelId = useId()

    return (
        <Stack
            direction="row"
            sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant="body1" id={labelId}>
                {label}
            </Typography>
            {actions}
        </Stack>
    )
}

type Props = BoxProps & {
    header: ReactNode
}

const Section = forwardRef(function Section(
    { header, sx, ...props }: Props,
    ref: ForwardedRef<HTMLDivElement>
) {
    const defaultLabelId = useId()

    return (
        <Paper component="section" aria-labelledby={header ? `${defaultLabelId}` : undefined}>
            {header}
            <Box ref={ref} sx={{ p: 2, ...sx }} {...props} />
        </Paper>
    )
})

type ComposedSection = typeof Section & {
    Header: typeof SectionHeader
}

const ComposedSection = Section as ComposedSection
ComposedSection.Header = SectionHeader

export default ComposedSection
